/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import React, { PropsWithChildren, useMemo } from 'react';
import { Box, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useSelector } from 'react-redux';
import { Quote } from '../../storage/types';
import TestIds from '../../../../testIds';
import { selectCurrentFarmerAccount } from '../../../azure-b2c/storage';
import { useContactInfo, ContactComponents } from '../../../drawdown/v1/forms/ContactInfo';
import { colors } from '@farmlands/mui-theme';
import { SubmitButton } from '../../../cart/components/CartSummaryComponents';
import Modal from '../../../../components/Modal';
import { ErrorMessage } from '../../../cart/utils/handleErrors';

export interface HandleSubmitQuoteParams {
  name: string;
  email: string;
  phone: string;
  reference?: string;
}

interface QuoteConfirmDetailsProps extends PropsWithChildren {
  quote: Quote;
  isModalOpen: boolean;
  setIsModalOpen: (open: boolean) => void;
  submitting: boolean;
  error?: boolean;
  handleSubmitQuote: (arg: HandleSubmitQuoteParams) => void;
}

export const QuoteConfirmDetails = ({
  quote,
  isModalOpen = false,
  setIsModalOpen,
  error = false,
  submitting = false,
  handleSubmitQuote,
}: QuoteConfirmDetailsProps) => {
  const currentAccount = useSelector(selectCurrentFarmerAccount);
  const isTfo = currentAccount?.hasTfoAccess || false;
  const theme = useTheme();
  const isLg = useMediaQuery(theme.breakpoints.up('lg'));

  const scrollToBottom = () => {
    if (!isLg) {
      const mc = document.getElementById('main-scroll-container');
      if (mc) {
        const scrollDiff = mc.clientHeight + mc.scrollTop;
        if (scrollDiff < mc.scrollHeight - 20) {
          mc.scrollTo({
            behavior: 'smooth',
            top: mc.scrollHeight + 100,
            left: 0,
          });
        }
      }
    }
  };

  const { email, phone, name, reference, validateContactInfo, ContactInfoComponent } =
    useContactInfo({
      components: [
        ContactComponents.NAME,
        ContactComponents.EMAIL,
        ContactComponents.PHONE,
        ContactComponents.REFERENCE,
      ],
      isTfo,
    });

  const onSubmit = () => {
    if (validateContactInfo() && currentAccount && quote) {
      scrollToBottom();
      if (!name || !email || !phone) return;
      handleSubmitQuote({ email, name, phone, reference });
    }
  };

  const getModal = useMemo(() => {
    const message = `Please provide contact details${isTfo ? ' for the customer' : ''}, in case a Farmlands representative needs to contact ${isTfo ? 'them' : 'you'} regarding this quote.`;

    const shareholderDetails = isTfo ? (
      <Stack direction='row' spacing={1} mb={3}>
        <Box bgcolor={colors.primary500} width='4px' alignSelf='stretch'></Box>
        <Stack direction='column'>
          <Typography variant='body1' color={colors.grey800} fontWeight='700'>
            {currentAccount?.accountName}
          </Typography>
          <Typography variant='body2' color={colors.grey700}>
            {currentAccount?.accountNumber}
          </Typography>
        </Stack>
      </Stack>
    ) : null;

    return (
      <Modal
        title={isTfo ? 'Confirm customer details' : 'Confirm your details'}
        open={isModalOpen}
        data-testid={TestIds.feat.checkout.contactModal}
        handleClose={() => {
          setIsModalOpen(false);
        }}
        header={<Typography variant='body1'>{message}</Typography>}
        footer={
          <SubmitButton
            disabled={submitting}
            variant='contained'
            type='submit'
            onClick={() => onSubmit()}
            data-testid={TestIds.feat.checkout.contactModalSubmit}
          >
            Submit Order
          </SubmitButton>
        }
      >
        {shareholderDetails}
        {ContactInfoComponent}
        {error && (
          <Typography
            variant='body2'
            color='error'
            data-testid={TestIds.feat.requestQuote.submitError}
          >
            {ErrorMessage.unknown}
          </Typography>
        )}
      </Modal>
    );
  }, [isModalOpen, onSubmit, error]);

  return getModal;
};
